import { engeniApi } from '@/api/engeni.api';
import type { AxiosResponse } from 'axios';
import { shallowReactive } from 'vue';

type Operation = { kind: string; request_key: string; status: string };
const requests = shallowReactive(
  new Map<
    string,
    { key: string; accepted?: boolean; completed?: boolean; pending?: Promise<AxiosResponse> }
  >(),
);
const storageKey = (path: string) => `billing-intent:${path}`;
const terminal = [
  'done',
  'failed',
  'retryable',
  'void',
  'no_payment',
  'not_required',
  'superseded',
];
const chargeRetryPath = (accountId: number, invoiceId: number) =>
  `/cxm/accounts/${accountId}/invoices/${invoiceId}/retry-charge`;

function release(path: string): void {
  const intent = requests.get(path);
  if (intent) intent.completed = true;
  try {
    sessionStorage.removeItem(storageKey(path));
  } catch {
    // Retain completion in memory until the durable key can also be removed.
    return;
  }
  requests.delete(path);
}

// Persist before sending so a lost response followed by reload cannot invent a new intent.
export function postInvoiceChargeRetry(
  accountId: number,
  invoiceId: number,
): Promise<AxiosResponse> {
  const path = chargeRetryPath(accountId, invoiceId);
  let intent = requests.get(path);
  if (intent?.pending) return intent.pending;
  if (intent?.completed) {
    release(path);
    if (requests.has(path))
      return Promise.reject(new Error('Unable to clear completed billing request'));
    intent = undefined;
  }
  try {
    const saved = sessionStorage.getItem(storageKey(path));
    intent ??= { key: saved ?? crypto.randomUUID(), accepted: saved !== null };
    sessionStorage.setItem(storageKey(path), intent.key);
  } catch (error) {
    return Promise.reject(error);
  }
  requests.set(path, intent);
  const current = intent;
  current.pending = engeniApi
    .post(path, undefined, { headers: { 'Idempotency-Key': current.key } })
    .then((response) => {
      current.accepted = true;
      const invoice = response.data?.data;
      if (response.status === 200 && invoice?.engine_owner === 'legacy') release(path);
      else if (
        invoice?.billing_operations?.some(
          (op: Operation) =>
            op.kind === 'charge' && op.request_key === current.key && terminal.includes(op.status),
        )
      )
        release(path);
      return response;
    })
    .catch((error) => {
      if (!error.response || error.response.status >= 500) current.accepted = true;
      else if (!current.accepted) release(path);
      throw error;
    })
    .finally(() => {
      current.pending = undefined;
    });
  return current.pending;
}

// A stored key can precede its operation appearing in an invoice read.
export function pendingInvoiceChargeRetryKey(
  accountId: number,
  invoiceId: number,
): string | undefined {
  const path = chargeRetryPath(accountId, invoiceId);
  const intent = requests.get(path);
  if (intent) return intent.completed ? undefined : intent.key;
  try {
    return sessionStorage.getItem(storageKey(path)) ?? undefined;
  } catch {
    return undefined;
  }
}

// Invoice status alone can still describe the previous attempt.
export function observeInvoiceChargeRetry(
  accountId: number,
  invoiceId: number,
  invoice: { billing_operations?: Operation[] },
): void {
  const path = chargeRetryPath(accountId, invoiceId);
  if (requests.get(path)?.pending) return;
  const key = pendingInvoiceChargeRetryKey(accountId, invoiceId);
  if (
    key &&
    invoice.billing_operations?.some(
      (op) => op.kind === 'charge' && op.request_key === key && terminal.includes(op.status),
    )
  ) {
    requests.set(path, { key, completed: true });
    release(path);
  }
}
